import type { AudienceMode } from "../lib/audienceMode";
import type { CostEstimate, OptimizationResult, Workload } from "../lib/types";
import type { Locale } from "./locale";

type Lang = "uk" | "en" | "es";

function langOf(locale: Locale): Lang {
  if (locale === "uk") return "uk";
  if (locale === "es") return "es";
  return "en";
}

export function buildOptimizationResult(
  w: Workload,
  estimate: CostEstimate,
  locale: Locale,
  audience: AudienceMode = "technical",
): OptimizationResult {
  const llm = estimate.lineItems.find((x) => x.id === "llm");
  const dynamo = estimate.lineItems.find((x) => x.id === "dynamo");
  const llmShare = llm ? llm.monthlyUsd / estimate.totalMonthlyUsd : 0;
  const L = langOf(locale);
  const simple = audience === "simple";

  const problems = [];
  if (llmShare >= 0.5) {
    problems.push({
      severity: "high" as const,
      title:
        simple && L === "uk"
          ? "Найбільша частина витрат — «розумні» відповіді"
          : simple && L === "es"
            ? "La mayor parte del coste suele ser la IA"
            : simple
              ? "Most of the bill is likely AI answers"
              : L === "uk"
                ? "AI cost домінує"
                : "AI cost dominates",
      detail:
        simple && L === "uk"
          ? `Понад половина орієнтиру припадає на відповіді штучного інтелекту. Якщо аудиторія виросте, ця стаття росте швидше за звичайний сервер.`
          : simple && L === "es"
            ? `Más de la mitad del total va a IA. Si crece el tráfico, esta partida suele subir más rápido que el cómputo normal.`
            : simple
              ? `Over half of the indicative total is AI. If usage grows, this line usually rises faster than plain servers.`
              : L === "uk"
                ? `~${Math.round(llmShare * 100)}% бюджету — найбільший ризик при рості трафіку.`
                : `~${Math.round(llmShare * 100)}% of budget — biggest risk as traffic grows.`,
    });
  } else if (llmShare >= 0.35) {
    problems.push({
      severity: "medium" as const,
      title:
        simple && L === "uk"
          ? "Помітна частка витрат на AI"
          : simple && L === "es"
            ? "La IA pesa bastante en el total"
            : simple
              ? "AI is a large slice of spend"
              : L === "uk"
                ? "LLM помітна частка витрат"
                : "LLM is a large share of spend",
      detail:
        simple && L === "uk"
          ? "Варто заздалегідь домовитись про ліміти довжини відповідей і повторне використання типових відповідей, щоб не роздути бюджет."
          : simple && L === "es"
            ? "Conviene acordar límites de respuesta y reutilizar respuestas típicas antes de escalar usuarios."
            : simple
              ? "Agree response limits and reuse common answers before you scale users."
              : L === "uk"
                ? "Варто закласти кешування та контроль токенів до масштабування."
                : "Plan caching and token controls before scaling up.",
    });
  }

  if (w.enabledFeatures.includes("search") && dynamo) {
    problems.push({
      severity: "medium" as const,
      title:
        simple && L === "uk"
          ? "Пошук може бути «важким» для бази"
          : simple && L === "es"
            ? "La búsqueda puede estresar la base de datos"
            : simple
              ? "Search can stress the database"
              : L === "uk"
                ? "Читання DynamoDB можуть бути неефективні"
                : "DynamoDB reads may be inefficient",
      detail:
        simple && L === "uk"
          ? "Звичайна таблиця не завжди зручна для повнотекстового пошуку. Команда часто додає окремий пошуковий сервіс або кеш."
          : simple && L === "es"
            ? "Una tabla genérica no siempre sirve para búsqueda de texto completo; suele añadirse un índice o caché aparte."
            : simple
              ? "A generic table is not always ideal for full-text search; teams often add a dedicated index or cache."
              : L === "uk"
                ? "Повнотекстовий пошук часто краще винести в OpenSearch / індекс, а не скан таблиці."
                : "Full-text search often belongs in OpenSearch / a search index, not table scans.",
    });
  }

  if (w.requestsPerUser > 200 && w.aiRequestShare > 0.25) {
    problems.push({
      severity: "medium" as const,
      title:
        simple && L === "uk"
          ? "Багато звернень до AI на одного користувача"
          : simple && L === "es"
            ? "Muchas llamadas a la IA por usuario"
            : simple
              ? "Many AI calls per user"
              : L === "uk"
                ? "Багато AI-викликів на користувача"
                : "Many AI calls per user",
      detail:
        simple && L === "uk"
          ? "Перевір, чи кожна дія справді потребує «розумної» відповіді — інколи достатньо шаблону або кешу."
          : simple && L === "es"
            ? "¿Cada acción necesita IA? A veces basta una plantilla o caché."
            : simple
              ? "Does every action need AI? Sometimes templates or cache are enough."
              : L === "uk"
                ? "Перевір, чи кожен запит справді потребує LLM."
                : "Check whether every request truly needs an LLM.",
    });
  }

  const suggestions = [];
  if (llmShare >= 0.35) {
    suggestions.push({
      title:
        simple && L === "uk"
          ? "Повторно використовувати типові відповіді"
          : simple && L === "es"
            ? "Reutilizar respuestas frecuentes"
            : simple
              ? "Reuse frequent answers"
              : L === "uk"
                ? "Кеш відповідей (semantic / exact)"
                : "Response cache (semantic / exact)",
      impact:
        simple && L === "uk"
          ? "Може суттєво зменшити кількість платних звернень до AI"
          : simple && L === "es"
            ? "Puede recortar muchas llamadas de pago a la IA"
            : simple
              ? "Can cut many paid AI calls"
              : L === "uk"
                ? "Часто −30–50% LLM викликів на повторювані запити"
                : "Often −30–50% LLM calls on repeated queries",
      effort: "medium" as const,
    });
    suggestions.push({
      title:
        simple && L === "uk"
          ? "Коротші запити та відповіді"
          : simple && L === "es"
            ? "Preguntas y respuestas más cortas"
            : simple
              ? "Shorter questions and answers"
              : L === "uk"
                ? "Батчинг та стислі промпти"
                : "Batching and tighter prompts",
      impact:
        simple && L === "uk"
          ? "Менше обсягу — менша ціна за той самий функціонал"
          : simple && L === "es"
            ? "Menos texto suele implicar menos coste"
            : simple
              ? "Less text usually means lower cost"
              : L === "uk"
                ? "Менше токенів input/output"
                : "Fewer input/output tokens",
      effort: "low" as const,
    });
  }
  suggestions.push({
    title:
      simple && L === "uk"
        ? "Черги для пікових записів"
        : simple && L === "es"
          ? "Colas para picos de escritura"
          : simple
            ? "Queues for write spikes"
            : L === "uk"
              ? "SQS + batch Lambda для запису"
              : "SQS + batch Lambda for writes",
    impact:
      simple && L === "uk"
        ? "Розгладжує навантаження на базу в гарячі години"
        : simple && L === "es"
          ? "Alivia la base en horas punta"
          : simple
            ? "Smooths load on the database at busy times"
            : L === "uk"
              ? "Дешевші writes, менше гарячих шляхів"
              : "Cheaper writes, fewer hot paths",
    effort: "medium" as const,
  });
  if (w.enabledFeatures.includes("search")) {
    suggestions.push({
      title:
        simple && L === "uk"
          ? "Окремий пошук замість «всього в одній таблиці»"
          : simple && L === "es"
            ? "Búsqueda dedicada, no solo una tabla"
            : simple
              ? "Dedicated search, not only one big table"
              : L === "uk"
                ? "Окремий search index + async оновлення"
                : "Dedicated search index + async updates",
      impact:
        simple && L === "uk"
          ? "Зменшує дорогі читання з основного сховища"
          : simple && L === "es"
            ? "Menos lecturas caras del almacén principal"
            : simple
              ? "Fewer expensive reads from primary storage"
              : L === "uk"
                ? "Менше дорогих reads з основної таблиці"
                : "Fewer expensive reads on the primary table",
      effort: "high" as const,
    });
  }

  const architecture = [];
  if (w.enabledFeatures.includes("chat") && w.enabledFeatures.includes("ai")) {
    architecture.push({
      title:
        simple && L === "uk"
          ? "Типова схема: захищений вхід → обробка → база"
          : simple && L === "es"
            ? "Esquema típico: entrada segura → proceso → base"
            : simple
              ? "Typical shape: secure entry → processing → database"
              : "API Gateway → Lambda (stateless) + DynamoDB",
      rationale:
        simple && L === "uk"
          ? "Повідомлення зберігаються в базі; важкі кроки з AI краще не тримати в одному довгому кроці — їх розбивають на частини."
          : simple && L === "es"
            ? "Los mensajes viven en la base; lo pesado con IA suele trocearse en pasos."
            : simple
              ? "Messages live in the database; heavy AI work is often split into steps."
              : L === "uk"
                ? "Чат + AI: тримай сесію в DDB, винеси довгі задачі в SQS / окремий worker Lambda."
                : "Chat + AI: keep session in DDB; offload long work to SQS / a separate worker Lambda.",
    });
  }
  if (estimate.totalMonthlyUsd > 2000) {
    architecture.push({
      title:
        simple && L === "uk"
          ? "При високому постійному навантаженні рахуй альтернативи"
          : simple && L === "es"
            ? "Con carga alta y estable, compara alternativas"
            : simple
              ? "At high steady load, compare alternatives"
              : L === "uk"
                ? "Розглянь Fargate / ECS для стабільного навантаження"
                : "Consider Fargate / ECS for steady load",
      rationale:
        simple && L === "uk"
          ? "Коли трафік рівний і великий, інколи вигідніше фіксовані «контейнери», ніж лише короткі функції — це питання до інженерів з цифрами."
          : simple && L === "es"
            ? "Con tráfico estable, a veces ganan contenedores fijos frente a solo funciones cortas — lo debe validar el equipo técnico."
            : simple
              ? "Steady traffic sometimes suits fixed containers better than only short-lived functions — engineers should model it."
              : L === "uk"
                ? "При високому постійному RPS Lambda + API GW можуть програвати за $/стабільність — порахуй break-even."
                : "At high steady RPS, Lambda + API GW may lose on $/stability — model the break-even.",
    });
  }

  const costPerformanceNote =
    L === "uk"
      ? simple
        ? ({
            cheaper:
              "Дешевший варіант: менше зайвих звернень до AI, простіші відповіді, без надлишкових серверів.",
            balanced:
              "Збалансовано: стандартний захищений вхід, автоматичне масштабування, AI лише там, де це видно користувачу.",
            faster:
              "Швидше для користувача: більше заздалегідь підготовлених відповідей і потужніший обчислювальний шар — зазвичай дорожче.",
          } as const)
        : ({
            cheaper:
              "Пріоритет: SQS батчі, кеш, мінімальні токени, on-demand DDB, менший розмір Lambda.",
            balanced:
              "API GW + Lambda + DDB + CloudFront на статику; LLM тільки там, де дає продуктову цінність.",
            faster:
              "Provisioned concurrency для Lambda, кеш ближче до клієнта, read replicas / кеш шару для hot keys, коротші моделі де можна.",
          } as const)
      : L === "es"
        ? simple
          ? ({
              cheaper:
                "Más barato: menos llamadas a la IA innecesarias, respuestas simples, sin capacidad de más.",
              balanced:
                "Equilibrado: entrada segura habitual, escalado automático, IA solo donde el usuario lo nota.",
              faster:
                "Más rápido: más respuestas preparadas y más potencia de cómputo — suele costar más.",
            } as const)
          : ({
              cheaper:
                "Prioridad: lotes SQS, caché, menos tokens, DDB on-demand, Lambda más pequeña.",
              balanced:
                "API GW + Lambda + DDB + CloudFront para estático; LLM solo donde aporta valor.",
              faster:
                "Provisioned concurrency, caché cerca del cliente, réplicas/caché para hot keys, modelos más pequeños donde se pueda.",
            } as const)
        : simple
          ? ({
              cheaper:
                "Cheaper path: fewer unnecessary AI calls, simpler answers, no oversized capacity.",
              balanced:
                "Balanced: standard secure entry, auto-scaling, AI only where users feel it.",
              faster:
                "Faster UX: more pre-built answers and more compute headroom — usually costs more.",
            } as const)
          : ({
              cheaper:
                "Priority: SQS batches, cache, minimal tokens, on-demand DDB, smaller Lambda.",
              balanced:
                "API GW + Lambda + DDB + CloudFront for static; LLM only where it drives product value.",
              faster:
                "Lambda provisioned concurrency, cache closer to clients, read replicas / cache layer for hot keys, smaller models where possible.",
            } as const);

  return {
    problems,
    suggestions,
    architecture,
    costPerformanceNote,
  };
}
